import { useCallback, useEffect, useRef } from 'react';

import { UseFormReturn } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import useAbbreviationSettings from 'api/useAbbreviationSettings';
import { AxiosError } from 'axios';
import { systemTexts } from 'constants/localization';
import { EToasterStatus, showToast } from 'utils/showToast';

import { IStoreSettingsFormValues } from '../../../StoreSettings.types';
import { toWirePayload } from '../AbbreviationsSettingsV2.payload';
import {
  SettingsFieldPath,
  SettingsFieldValue
} from '../AbbreviationsSettingsV2.types';

const AUTOSAVE_DEBOUNCE_MILLISECONDS = 600;

interface IUseAbbreviationsAutosaveProps {
  form: UseFormReturn<IStoreSettingsFormValues>;
}

interface IUseAbbreviationsAutosaveReturn {
  commitField: (path: SettingsFieldPath, value: SettingsFieldValue) => void;
  isSaving: boolean;
}

/**
 * Wires Store Settings abbreviation fields to an auto-saving React Hook Form.
 * Each committed change updates the form and schedules a single debounced
 * `updateAbbreviationSettings` mutation with the latest, validated form values.
 *
 * @example
 * const form = useForm<IStoreSettingsFormValues>({ values });
 * const { commitField, isSaving } = useAbbreviationsAutosave({ form });
 * commitField('numberFormat.products.quantity.thousandShorthand', true);
 */
export const useAbbreviationsAutosave = ({
  form
}: IUseAbbreviationsAutosaveProps): IUseAbbreviationsAutosaveReturn => {
  const { publisherId } = useParams();
  const { updateAbbreviationSettings } = useAbbreviationSettings(publisherId);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const persistLatestValues = useCallback(async (): Promise<void> => {
    const isValid = await form.trigger();
    if (!isValid) {
      return;
    }
    try {
      await updateAbbreviationSettings.mutateAsync(
        toWirePayload(form.getValues().numberFormat)
      );
    } catch (error) {
      showToast({
        message: systemTexts.settingsTexts.THEME_UPDATE_ERROR,
        status: EToasterStatus.ERROR,
        description:
          ((error as AxiosError).response?.data as { message: string })
            ?.message || 'Unknown error occurred'
      });
    }
  }, [form, updateAbbreviationSettings]);

  const commitField = useCallback(
    (path: SettingsFieldPath, value: SettingsFieldValue): void => {
      const setSettingsField = form.setValue as unknown as (
        path: SettingsFieldPath,
        value: SettingsFieldValue,
        options: { shouldDirty: boolean; shouldValidate: boolean }
      ) => void;
      setSettingsField(path, value, {
        shouldDirty: true,
        shouldValidate: true
      });
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(
        persistLatestValues,
        AUTOSAVE_DEBOUNCE_MILLISECONDS
      );
    },
    [form, persistLatestValues]
  );

  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    []
  );

  return { commitField, isSaving: updateAbbreviationSettings.isLoading };
};
